/**
 * Salle temps réel : LWW (v, by), adoption, présence, heartbeat — porté du prototype
 * (index.html:2072-2154). Indépendant de l'UI et des moteurs : tout passe par `RoomCallbacks`.
 */
import type { Snapshot, StateMsg } from '@ordre-cache/shared';
import { genRoomCode, parseRoomMsg } from '@ordre-cache/shared';
import type { NetEventType, Transport, TransportFactory } from './transport';

/** Identité locale, stable pour la session (index.html:2072). */
export const MYID = Math.random().toString(36).slice(2, 8);

const HEARTBEAT_MS = 2000;
const PEER_TTL_MS = 6000;

/**
 * Code de salle local — pour le transport BroadcastChannel (tests/dev hors-ligne), qui n'a
 * pas de serveur pour en générer. Le transport WebSocket, lui, reçoit le code du serveur
 * (`POST /rooms`, docs/03 §2.5). Délègue à `genRoomCode` partagé.
 */
export function genCode(): string {
  return genRoomCode();
}

export interface RoomCallbacks {
  /** Snapshot de l'état local (screen, diff, sizeIdx, game, defi, puz). */
  buildSnapshot(): Snapshot;
  /** Applique un snapshot distant adopté (mutation d'état + rendu). */
  applyRemote(snap: Snapshot): void;
  /** Nombre de joueurs présents (soi inclus). */
  onPresence(count: number): void;
  /** Le transport est ouvert. */
  onOpen(code: string): void;
  /** Erreur de transport (ex. `room_not_found`, `room_full`, `network`). */
  onError(type: string): void;
  /** Connexion perdue (fermeture inattendue). */
  onConnectionLost(): void;
}

export interface Room {
  push(): void;
  leave(): void;
  code(): string;
  connected(): boolean;
  adopted(): boolean;
  peerCount(): number;
}

function errType(data: unknown): string {
  if (data && typeof data === 'object' && 'type' in data) {
    return String((data as { type: unknown }).type);
  }
  return String(data ?? '');
}

export function createRoom(params: {
  creator: boolean;
  code: string;
  make: TransportFactory;
  cb: RoomCallbacks;
}): Room {
  const { creator, code, make, cb } = params;
  let net: Transport | null = null;
  let adopted = !!creator;
  let applying = false;
  let lastV = creator ? 1 : 0;
  let lastBy = creator ? MYID : '';
  const peers = new Map<string, number>();
  let hb: ReturnType<typeof setInterval> | null = null;

  const presence = (): void => cb.onPresence(peers.size + 1);

  function snapshotMsg(v: number, by: string): StateMsg {
    return { t: 'state', from: MYID, by, v, ...cb.buildSnapshot() };
  }

  function push(): void {
    if (net && adopted && !applying) {
      lastV++;
      lastBy = MYID;
      net.send(snapshotMsg(lastV, MYID));
    }
  }

  function applyState(d: StateMsg): void {
    applying = true;
    cb.applyRemote({
      screen: d.screen,
      diff: d.diff,
      sizeIdx: d.sizeIdx,
      game: d.game,
      defi: d.defi,
      puz: d.puz,
    });
    applying = false;
  }

  function onMsg(raw: unknown): void {
    const d = parseRoomMsg(raw);
    if (!d) return;
    const sid = d.t === 'state' ? d.from : d.by; // expéditeur réel (from) ≠ auteur (by)
    if (sid && sid !== MYID) {
      const isNew = !peers.has(sid);
      peers.set(sid, Date.now());
      if (isNew) presence();
    }
    if (sid === MYID) return;
    if (d.t === 'ping' && d.need && adopted && net)
      net.send(snapshotMsg(lastV || 1, lastBy || MYID));
    if (d.t === 'state') {
      if (!adopted) {
        adopted = true;
        lastV = d.v;
        lastBy = d.by;
        applyState(d);
      } else if (d.v > lastV || (d.v === lastV && d.by > lastBy)) {
        lastV = d.v;
        lastBy = d.by;
        applyState(d);
      }
    } else if (d.t === 'bye') {
      if (peers.delete(sid)) presence();
    }
  }

  function startHeartbeat(): void {
    stopHeartbeat();
    hb = setInterval(() => {
      if (!net) {
        stopHeartbeat();
        return;
      }
      net.send({ t: 'ping', by: MYID, need: !adopted });
      const now = Date.now();
      let ch = false;
      peers.forEach((ts, id) => {
        if (now - ts > PEER_TTL_MS) {
          peers.delete(id);
          ch = true;
        }
      });
      if (ch) presence();
    }, HEARTBEAT_MS);
  }
  function stopHeartbeat(): void {
    if (hb) {
      clearInterval(hb);
      hb = null;
    }
  }

  function leave(): void {
    if (net) {
      try {
        net.send({ t: 'bye', by: MYID });
      } catch {
        /* déjà fermé */
      }
      try {
        net.close();
      } catch {
        /* déjà fermé */
      }
    }
    net = null;
    adopted = false;
    lastV = 0;
    lastBy = '';
    peers.clear();
    stopHeartbeat();
  }

  function onEvent(type: NetEventType, data?: unknown): void {
    if (type === 'open') {
      cb.onOpen(code);
      net?.send({ t: 'ping', by: MYID, need: !adopted });
      if (creator) {
        adopted = true;
        lastV = Math.max(lastV, 1);
        lastBy = MYID;
        net?.send(snapshotMsg(lastV, MYID));
      }
      presence();
      startHeartbeat();
    } else if (type === 'msg') {
      onMsg(data);
    } else if (type === 'error') {
      cb.onError(errType(data));
    } else if (type === 'close') {
      if (net) {
        leave();
        cb.onConnectionLost();
      }
    }
  }

  net = make(code, MYID, onEvent);

  return {
    push,
    leave,
    code: () => code,
    connected: () => !!net,
    adopted: () => adopted,
    peerCount: () => peers.size + 1,
  };
}
